import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Select, message, ConfigProvider } from 'antd';
import { Form } from 'antd';
import { login, logout, triggerLoginStateChange } from 'redux/slices/authSlice';
import { getDomainList } from 'redux/slices/domainSlice';
import { resetsingleUserData, setSelectedItems } from 'redux/slices/userSlice';
import { GlobalOutlinedIcon, LockIcon, UserIcon } from 'assets/icons/AntDeisgn_icons/iconStyle';
import {
  ForgotPasswordItem,
  InputWrap,
  RememberMe,
  SelectWrapper,
  StyledInput,
  StyledInputPassword,
  StyledLink,
  StyledSelectInput,
  ThemeLoginButton,
} from 'pages/login/login_style';
import { SweetSweetAlert } from 'component/sweetalert_components';
import { setDomainName } from 'redux/slices/selectedNodeSlice';
import { getSettingPageDetails } from 'redux/slices/settingsSlice';
import LoginButtonLoader from 'component/loader_component/loginButtonLoader';
import Cookies from 'js-cookie';
import { setGroupSelectedItems } from 'redux/slices/getOuGroups';
import { setupGetLicenseDetails, setupStatus } from 'redux/slices/setupSlice';

export default function LoginComponent({ theme }) {
  const { loading, error } = useSelector((state) => state.authenticatedUser);
  const { forgetPassword, rememberMeSetting } = useSelector((state) => state.settings);
  const { domainList } = useSelector((state) => state.domainData);
  const { brandingData } = useSelector((state) => state.themes);
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const showDomains = brandingData ? brandingData.showDomains : true;
  const [canBrandingForgot, setCanBrandingForgot] = useState(null);
  const [canBrandingRemember, setCanBrandingRemember] = useState(null);
  const [domainId, setDomainId] = useState(null);
  const forgetPasswordshow = canBrandingForgot ?? forgetPassword;
  const rememberMeshow = canBrandingRemember ?? rememberMeSetting;

  useEffect(() => {
    const { hostname, port } = window.location;
    const currentDomain = `${hostname}${port ? `:${port}` : ''}`;
    if (currentDomain === brandingData?.url) {
      setCanBrandingForgot(brandingData.forgotPassword);
      setCanBrandingRemember(brandingData.rememberMe);
    }
  }, [brandingData]);

  useEffect(() => {
    const initialStatus = async () => {
      const checkSetupStatus = await dispatch(setupStatus());
      if (checkSetupStatus?.payload.data.domainstatus && checkSetupStatus?.payload.data.groupstatus) {
        const productLicense = await dispatch(setupGetLicenseDetails());
        if (productLicense?.payload === 'License details not found.') {
          clear();
        }
      } else {
        clear();
      }
    };
    const clear = () => {
      localStorage.clear();
      dispatch(logout());
      navigate('/setup');
    };
    initialStatus();
  }, [navigate, dispatch]);

  useEffect(() => {
    localStorage.removeItem('nextLogon');
    if (Cookies.get('access_token')) {
      navigate('/');
    }
  }, [navigate]);

  useEffect(() => {
    if (rememberMeSetting) {
      const storedUsername = localStorage.getItem('rememberedUsername');
      const storedPassword = localStorage.getItem('rememberedPassword');
      if (storedUsername && storedPassword) {
        setUsername(storedUsername);
        setPassword(storedPassword);
        setRememberMe(true);
      }
    }
  }, [rememberMeSetting]);

  useEffect(() => {
    dispatch(getDomainList(brandingData?.url || ''));
    dispatch(getSettingPageDetails());
  }, [brandingData]);

  useEffect(() => {
    if (domainList.length) {
      form.setFieldsValue({ fqdn: domainList[0].domainInfo });
      setDomainId(domainList[0].id);
    }
  }, [domainList]);
  const handleDomainChange = (value, option) => {
    setDomainId(Number(option.key));
  };

  const handleSubmit = async (values) => {
    if (rememberMe) {
      localStorage.setItem('rememberedUsername', username);
      localStorage.setItem('rememberedPassword', password);
    } else {
      localStorage.removeItem('rememberedUsername');
      localStorage.removeItem('rememberedPassword');
    }
    const inputString = values.fqdn ? values.fqdn : domainList[0].domainInfo;
    const fqdn = inputString.substring(inputString.indexOf('(') + 1, inputString.indexOf(')'));
    values.fqdn = fqdn;
    values.username = values.username.trim();
    try {
      const sweetAlert = await dispatch(login(values));
      if (sweetAlert.payload && sweetAlert.payload.status === 200) {
        if (sweetAlert.payload && sweetAlert.payload.data.message === 'User must change password at next logon.') {
          localStorage.setItem('nextLogon', true);
          navigate('/change_password');
        } else {
          if (sweetAlert?.payload.data.isLicense) {
            await message.open({
              type: 'success',
              content: sweetAlert.payload.data.successMessage,
              duration: 0.5,
            });
            dispatch(setDomainName(sweetAlert.payload.data.fqdn));
            dispatch(resetsingleUserData());
            if (sweetAlert.payload.data.userName === localStorage.getItem('previousAccount')) {
              navigate(localStorage.getItem('previousPath'));
              localStorage.removeItem('previousPath');
              localStorage.removeItem('previousAccount');
            } else {
              localStorage.setItem('domainId', domainId);
              triggerLoginStateChange();
              navigate('/');
            }
          } else {
            if (sweetAlert.payload.data.role === 'superadmin') {
              navigate('/license');
            } else {
              SweetSweetAlert({
                text: "Can't Process Your Request Please Contact Your Administrator",
                icon: 'info',
                buttonColor: 'blue',
              });
              dispatch(logout());
              dispatch(setSelectedItems([]));
              dispatch(setGroupSelectedItems([]));
            }
          }
        }
      } else {
        SweetSweetAlert({ text: sweetAlert.payload.data, icon: 'error', buttonColor: 'red' });
      }
    } catch (error) {
      SweetSweetAlert({ text: 'Something Wrong Please try again.!!', icon: 'error', buttonColor: 'red' });
    }
  };

  return (
    <div className='login-content-component'>
      <Form
        name='login'
        form={form}
        onFinish={handleSubmit}
        initialValues={{
          username: localStorage.getItem('rememberedUsername'),
          password: localStorage.getItem('rememberedPassword'),
        }}
        className='login-form-element'
      >
        <InputWrap className='login-input-wrapper'>
          {error && <p>{error.message} </p>}
          {showDomains && (
            <SelectWrapper className={`${theme} login-select-wrapper`}>
              <div className='prefix-icon-wrapper login-icon-wrap'>
                {<GlobalOutlinedIcon className='icon-color login-icon-color' />}
              </div>
              <Form.Item
                name='fqdn'
                rules={[{ required: true, message: 'Please enter the Domain name.' }]}
                className='login-form-item-fqdn'
              >
                <StyledSelectInput
                  className={`${theme} login-selectorbox-fqdn`}
                  size='large'
                  defaultActiveFirstOption
                  placeholder='Select Domain'
                  onChange={handleDomainChange}
                >
                  {domainList.length &&
                    domainList.map((item) => (
                      <Select.Option key={item.id} value={item.domainInfo} className='login-selectorbox-option-fqdn'>
                        {item.domainInfo.toLowerCase()}
                      </Select.Option>
                    ))}
                </StyledSelectInput>
              </Form.Item>
            </SelectWrapper>
          )}
          <ConfigProvider theme={{ components: { Input: {
            colorBgContainer: ['mornglow','teamring'].includes(theme) ? '#fff' : '#33455d',
            activeBg: ['mornglow','teamring'].includes(theme) ? '#fff' : '#33455d',
            hoverBorderColor: ['mornglow','teamring'].includes(theme) ? '#c2d2e3' : '#33455d',
            activeBorderColor: ['mornglow','teamring'].includes(theme) ? '#c2d2e3' : '#33455d',
            colorBorder: ['mornglow','teamring'].includes(theme) ? '#c2d2e3' : '#33455d',
            colorText: ['mornglow'].includes(theme) ? '#91a3af' : ['teamring'].includes(theme) ? '#3e4f60' : '#fff',
            colorTextPlaceholder: ['mornglow'].includes(theme) ? 'rgba(145,163,175,0.6)' : 'rgba(255,255,255,0.6)',
            colorIcon: ['mornglow'].includes(theme) ? '#91a3af' : ['teamring'].includes(theme) ? '#3e4f60' : '#fff',
            activeShadow: 'none',
            errorActiveShadow: 'none',
            warningActiveShadow: 'none',
          }}}}>
          <Form.Item
            name='username'
            rules={[{ required: true, message: 'Please enter the User name.' }]}
            className='login-form-item-username'
          >
            <StyledInput
              className={`${theme} login-input-username`}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              size='large'
              prefix={<UserIcon className='icon-color' />}
              placeholder='Username'
            />
          </Form.Item>
          <Form.Item
            name='password'
            rules={[
              {
                required: true,
                message: 'Please enter the Password.',
              },
            ]}
            className='login-form-item-password'
          >
            <StyledInputPassword
              className={`${theme} login-input-password`}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              size='large'
              prefix={<LockIcon className='icon-color' />}
              placeholder='Password'
            />
          </Form.Item>
          </ConfigProvider>
          {rememberMeshow && (
            <p className='login-rememberMe-wrap'>
              <RememberMe
                className={`${theme} login-rememberMe-box`}
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
              >
                Remember me
              </RememberMe>
            </p>
          )}
          <Form.Item className='login-form-item-submit'>
            <ThemeLoginButton className={`${theme} login-submit-button`} type='primary' disabled={loading}>
              {loading ? <LoginButtonLoader /> : 'Log in'}
            </ThemeLoginButton>
          </Form.Item>
          {forgetPasswordshow && (
            <ForgotPasswordItem disabled={loading} className='login-form-item-forgot'>
              <StyledLink className={`${theme} login-forgot-link`} to='/forgot_password'>
                Forgot Password ?
              </StyledLink>
            </ForgotPasswordItem>
          )}
        </InputWrap>
      </Form>
    </div>
  );
}
